import {
  Directive,
  HostListener,
  forwardRef,
  ElementRef,
  OnChanges,
  Input,
  inject,
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';

@Directive({
  selector: '[appSomenteLetras]',
})
export class SomenteLetrasDirective {
  private readonly regexLimpeza = /[^a-zA-ZÀ-ú\s]/g;

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const valorLimpo = input.value.replace(this.regexLimpeza, '');
    if (input.value !== valorLimpo) {
      input.value = valorLimpo;
      input.dispatchEvent(new Event('input'));
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const texto = event.clipboardData?.getData('text') || '';
    const textoLimpo = texto.replace(this.regexLimpeza, '');
    const input = event.target as HTMLInputElement;
    const inicio = input.selectionStart ?? input.value.length;
    const fim = input.selectionEnd ?? input.value.length;
    input.value = input.value.slice(0, inicio) + textoLimpo + input.value.slice(fim);
    input.dispatchEvent(new Event('input'));
  }
}

@Directive({
  selector: '[appSomenteNumero]',
})
export class SomenteNumerosDirective {
  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const valorLimpo = input.value.replace(/\D/g, '');
    if (input.value !== valorLimpo) {
      input.value = valorLimpo;
      input.dispatchEvent(new Event('input'));
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const texto = event.clipboardData?.getData('text') || '';
    const textoLimpo = texto.replace(/\D/g, '');
    const input = event.target as HTMLInputElement;
    const inicio = input.selectionStart ?? input.value.length;
    const fim = input.selectionEnd ?? input.value.length;
    input.value = input.value.slice(0, inicio) + textoLimpo + input.value.slice(fim);
    input.dispatchEvent(new Event('input'));
  }
}

@Directive({
  selector: '[appCnpjCpfTexto]',
})
export class CnpjCpfTextoDirective implements OnChanges {
  private el = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input('appCnpjCpfTexto') valor?: string | null;

  ngOnChanges(): void {
    const raw = (this.valor || '').replace(/\W/g, '');
    let out = this.valor ?? '-';
    if (raw.length === 11) {
      out = raw.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (raw.length === 14) {
      out = raw.replace(/(\w{2})(\w{3})(\w{3})(\w{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    this.el.nativeElement.textContent = out;
  }
}

@Directive({
  selector: '[appPlacaTexto]',
})
export class PlacaTextoDirective implements OnChanges {
  private el = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input('appPlacaTexto') valor?: string | null;

  ngOnChanges(): void {
    const raw = (this.valor || '').toUpperCase().trim();
    const out = raw.replace(/^([A-Z]{3})(\d{4})$/, '$1-$2') || (this.valor ?? '—');
    this.el.nativeElement.textContent = out;
  }
}

@Directive({
  selector: '[appSomenteAlfanumerico]',
})
export class SomenteAlfanumericoDirective {
  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const valorLimpo = input.value.replace(/[^a-zA-Z0-9À-ú\s]/g, '');
    if (input.value !== valorLimpo) {
      input.value = valorLimpo;
      input.dispatchEvent(new Event('input'));
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const texto = event.clipboardData?.getData('text') || '';
    const textoLimpo = texto.replace(/[^a-zA-Z0-9À-ú\s]/g, '');
    const input = event.target as HTMLInputElement;
    const inicio = input.selectionStart ?? input.value.length;
    const fim = input.selectionEnd ?? input.value.length;
    input.value = input.value.slice(0, inicio) + textoLimpo + input.value.slice(fim);
    input.dispatchEvent(new Event('input'));
  }
}

@Directive({
  selector: '[appSomenteData]',
})
export class SomenteDataDirective {
  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let valor = input.value;
    valor = valor.replace(/[^0-9/]/g, '');
    if (valor.length > 10) {
      valor = valor.slice(0, 10);
    }
    input.value = valor;
  }
}

@Directive({
  selector: '[appCpfValido]',
  standalone: true,
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CpfValidoDirective),
      multi: true,
    },
  ],
})
export class CpfValidoDirective implements Validator {
  validate(control: AbstractControl): ValidationErrors | null {
    const cpf = (control.value || '').replace(/\D/g, '');
    if (!cpf) return null;

    const repetidos = [
      '00000000000', '11111111111', '22222222222', '33333333333', '44444444444',
      '55555555555', '66666666666', '77777777777', '88888888888', '99999999999',
    ];
    if (repetidos.includes(cpf)) return { cpfInvalido: true };
    if (cpf.length !== 11) return { cpfInvalido: true };

    let soma = 0;
    for (let i = 0; i < 9; i++) soma += parseInt(cpf.charAt(i)) * (10 - i);
    let resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(9))) return { cpfInvalido: true };

    soma = 0;
    for (let i = 0; i < 10; i++) soma += parseInt(cpf.charAt(i)) * (11 - i);
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(10))) return { cpfInvalido: true };

    return null;
  }
}

@Directive({
  selector: '[appCnpjValido]',
  standalone: true,
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CnpjValidoDirective),
      multi: true,
    },
  ],
})
export class CnpjValidoDirective implements Validator {
  validate(control: AbstractControl): ValidationErrors | null {
    const cnpj = (control.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!cnpj) return null;
    if (cnpj.length !== 14) return { cnpjInvalido: true };
    if (/^(\d)\1{13}$/.test(cnpj)) return { cnpjInvalido: true };

    const charToValue = (char: string): number => (char.codePointAt(0) ?? 0) - 48;
    const calcDigito = (base: string, pesos: number[]): number => {
      let soma = 0;
      for (let i = 0; i < pesos.length; i++) soma += charToValue(base.charAt(i)) * pesos[i];
      const resto = soma % 11;
      return resto < 2 ? 0 : 11 - resto;
    };

    const base = cnpj.slice(0, 12);
    const digito1 = calcDigito(base, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
    const digito2 = calcDigito(base + digito1, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);

    if (charToValue(cnpj.charAt(12)) !== digito1 || charToValue(cnpj.charAt(13)) !== digito2) {
      return { cnpjInvalido: true };
    }
    return null;
  }
}

@Directive({
  selector: '[appRgValido]',
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RgValidoDirective),
      multi: true,
    },
  ],
})
export class RgValidoDirective implements Validator {
  validate(control: AbstractControl): ValidationErrors | null {
    const rg = (control.value || '').replaceAll(/\D/g, '');
    if (!rg) return null;

    const repetidos = [
      '000000000', '111111111', '222222222', '333333333', '444444444',
      '555555555', '666666666', '777777777', '888888888', '999999999',
    ];
    if (repetidos.includes(rg)) return { rgInvalido: true };
    if (rg.length < 7 || rg.length > 9) return { rgInvalido: true };
    return null;
  }
}

@Directive({
  selector: '[appCelularValido]',
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CelularValidoDirective),
      multi: true,
    },
  ],
})
export class CelularValidoDirective implements Validator {
  validate(control: AbstractControl): ValidationErrors | null {
    const celular = (control.value || '').replaceAll(/\D/g, '');
    if (!celular) return null;

    const repetidos = [
      '00000000000', '11111111111', '22222222222', '33333333333', '44444444444',
      '55555555555', '66666666666', '77777777777', '88888888888', '99999999999',
    ];
    if (repetidos.includes(celular)) return { celularInvalido: true };
    if (celular.length !== 11) return { celularInvalido: true };
    return null;
  }
}

@Directive({
  selector: '[appEmailValido]',
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => EmailValidoDirective),
      multi: true,
    },
  ],
})
export class EmailValidoDirective implements Validator {
  validate(control: AbstractControl): ValidationErrors | null {
    const email = (control.value || '').toLowerCase();
    if (!email) return null;
    const regex = /^[^\s@]+@[^\s@]+\.(com|br)$/;
    return regex.test(email) ? null : { emailInvalido: true };
  }
}

@Directive({
  selector: '[appFormatarData]',
})
export class FormatarDataDirective {
  private el = inject<ElementRef<HTMLInputElement>>(ElementRef);

  @HostListener('input')
  onInput() {
    let valor = this.el.nativeElement.value.replaceAll(/\D/g, '');

    if (valor.length > 2) valor = valor.slice(0, 2) + '/' + valor.slice(2);
    if (valor.length > 5) valor = valor.slice(0, 5) + '/' + valor.slice(5);
    if (valor.length > 10) valor = valor.slice(0, 10);

    const partes = valor.split('/');
    if (partes.length === 3) {
      const [dia, mes, ano] = partes;
      const repetidos = new Set([dia, mes, ano]).size === 1;
      const invalida = !this.DataValida(dia, mes, ano);
      if (repetidos || invalida) valor = '';
    }

    this.el.nativeElement.value = valor;
    const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
    setter?.call(this.el.nativeElement, valor);
    this.el.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private DataValida(dia: string, mes: string, ano: string): boolean {
    if (dia.length !== 2 || mes.length !== 2 || ano.length !== 4) return false;
    const d = parseInt(dia, 10);
    const m = parseInt(mes, 10);
    const a = parseInt(ano, 10);
    if (isNaN(d) || isNaN(m) || isNaN(a)) return false;
    if (d < 1 || m < 1 || m > 12) return false;
    const diasNoMes = new Date(a, m, 0).getDate();
    return d <= diasNoMes;
  }
}

@Directive({
  selector: '[appMascaraTelefone]',
})
export class MascaraTelefoneDirective {
  private el = inject<ElementRef<HTMLInputElement>>(ElementRef);

  private formatar(digits: string): string {
    // digits = somente números, ex: 5511999999999
    if (digits.length <= 7) return digits;

    if (digits.length === 13) {
      // +55 (11) 99999-9999
      return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
    }
    if (digits.length === 12) {
      // +55 (11) 9999-9999
      return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
    }
    if (digits.length === 14) {
      // +351 (11) 99999-9999
      return `+${digits.slice(0, 3)} (${digits.slice(3, 5)}) ${digits.slice(5, 10)}-${digits.slice(10)}`;
    }
    // fallback: só exibe os dígitos com o +
    return `+${digits}`;
  }

  @HostListener('focus')
  onFocus() {
    const raw = this.el.nativeElement.value.replace(/\D/g, '');
    this.el.nativeElement.value = raw ? `+${raw}` : '';
  }

  @HostListener('blur')
  onBlur() {
    const raw = this.el.nativeElement.value.replace(/\D/g, '');
    // Mínimo: DDI(1-3) + DDD(2) + número(8) = 11 dígitos
    if (!raw || raw.length < 11) {
      this.el.nativeElement.value = raw ? `+${raw}` : '';
      return;
    }
    this.el.nativeElement.value = this.formatar(raw);
  }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const temPlus = input.value.startsWith('+');
    const digits = input.value.replace(/\D/g, '').slice(0, 13); // máximo 13
    const novo = temPlus || digits ? `+${digits}` : '';
    if (input.value !== novo) {
      input.value = novo;
      input.dispatchEvent(new Event('input'));
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const texto = event.clipboardData?.getData('text') || '';
    const digits = texto.replace(/\D/g, '').slice(0, 15);
    if (digits) {
      this.el.nativeElement.value = `+${digits}`;
      this.el.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}
